import React, { useState, useRef } from 'react';
import { Upload, X, FileText, Plus, Eye, FileImage, IdCard, FileCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { TeacherDocument } from '@/types/teacher.types';
import { documentTrackingService } from '@/utils/notifications/documents/DocumentTrackingService';

interface TeacherDocumentsUploadProps {
  formData: any;
  handleDocumentUpload: (files: File[], documentType?: string) => void;
  removeDocument?: (index: number) => void;
  viewDocument?: (document: TeacherDocument) => void;
}

const TeacherDocumentsUpload = ({ 
  formData, 
  handleDocumentUpload, 
  removeDocument,
  viewDocument
}: TeacherDocumentsUploadProps) => {
  const [dragging, setDragging] = useState(false);
  const [documentType, setDocumentType] = useState('general');
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };
  
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };
  
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      handleDocumentUpload(files, documentType);
    }
  };
  
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      
      const selectedDocType = documentTypes.find(type => type.value === documentType);
      if (selectedDocType?.requiresExpiry) {
        const expiryDate = prompt('Please enter expiry date (YYYY-MM-DD):');
        if (!expiryDate) {
          toast.error('Expiry date is required for this document type');
          return;
        }
        
        handleDocumentUpload(files, documentType);
        
        if (formData.id) {
          const newDoc = formData.documents[formData.documents.length - 1];
          if (newDoc) {
            await documentTrackingService.checkDocumentExpiry(
              { ...newDoc, expirationDate: expiryDate },
              formData.id
            );
          }
        }
      } else {
        handleDocumentUpload(files, documentType);
      }
      
      e.target.value = '';
    }
  };
  
  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  const handleRemoveDocument = (index: number) => {
    if (removeDocument) {
      removeDocument(index);
    } else {
      const updatedDocuments = [...(formData.documents || [])];
      updatedDocuments.splice(index, 1);
      formData.documents = updatedDocuments;
      toast.success("Document removed successfully");
    }
  };

  const handleViewDocument = (document: TeacherDocument) => {
    if (viewDocument) {
      viewDocument(document);
    } else {
      if (document.url) {
        window.open(document.url, '_blank');
      }
    }
  };
  
  const documentTypes = [
    { 
      value: 'contract', 
      label: 'Contract (Requires Expiry Date)', 
      icon: FileText,
      requiresExpiry: true 
    },
    { 
      value: 'passport', 
      label: 'Passport (Requires Expiry Date)', 
      icon: IdCard,
      requiresExpiry: true 
    },
    { 
      value: 'certificate', 
      label: 'Certificate (Requires Expiry Date)', 
      icon: FileCheck,
      requiresExpiry: true 
    },
    { 
      value: 'other', 
      label: 'Other Document', 
      icon: FileImage,
      requiresExpiry: false 
    }
  ];
  
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="md:col-span-3">
          <Label htmlFor="document-type">Document Type</Label>
          <Select
            value={documentType}
            onValueChange={(value) => setDocumentType(value)}
          >
            <SelectTrigger id="document-type">
              <SelectValue placeholder="Select document type" />
            </SelectTrigger>
            <SelectContent>
              {documentTypes.map(type => (
                <SelectItem 
                  key={type.value} 
                  value={type.value}
                  className="flex items-center gap-2"
                >
                  <span className="flex items-center gap-2">
                    <type.icon className="h-4 w-4" />
                    {type.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center ${
          dragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="flex flex-col items-center">
          <Upload className="h-8 w-8 text-gray-400 mb-2" />
          <p className="text-sm text-gray-600 mb-2">
            Drag and drop documents here or click to browse
          </p>
          
          <Button
            type="button"
            variant="default"
            onClick={handleButtonClick}
            className="mb-2"
          >
            <Plus className="mr-2 h-4 w-4" /> Select Documents
          </Button>
          
          <Input
            id="document-upload"
            type="file"
            multiple
            className="hidden"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          />
          
          <p className="text-xs text-gray-400 mt-2">
            Supports PDF, DOC, DOCX, JPG, PNG (max 5MB)
          </p>
        </div>
      </div>
      
      {formData.documents && formData.documents.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium">Uploaded Documents</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {formData.documents.map((doc: TeacherDocument, index: number) => (
              <div 
                key={doc.id || index} 
                className="flex items-center justify-between p-3 border rounded bg-gray-50 hover:bg-gray-100"
              >
                <div className="flex items-center overflow-hidden">
                  <FileText className="h-5 w-5 mr-2 text-blue-500 flex-shrink-0" />
                  <div className="overflow-hidden">
                    <span className="text-sm font-medium block truncate">{doc.name || `Document ${index + 1}`}</span>
                    <span className="text-xs text-gray-500 block">{doc.type || 'Unknown type'}</span>
                  </div>
                </div>
                <div className="flex space-x-1">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-blue-500 hover:text-blue-700"
                    onClick={() => handleViewDocument(doc)}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-red-500 hover:text-red-700"
                    onClick={() => handleRemoveDocument(index)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherDocumentsUpload;
